import { sentence, whom } from '../core/words.js';
import { ft } from '../core/units.js';
import { segmentClear } from '../core/collision.js';
import { CART, NEAR, POSSIBLE } from './prompts.js';
import { EVENT } from './events.js';

/**
 * Who is in the golf cart with you, and what happens to them.
 *
 * Offering somebody a lift, letting them off, getting out, the trail they walk
 * back along, and the one thing done when a branch reaches them. Moved out of
 * `main.js` whole; the wheeled class decides where the seats are, this decides
 * who is in them.
 */
export function createRiders({ wheeled, npcs, player, level, bus, hud, autosave, think = () => {} }) {
  /**
   * What a branch or a bush costs you with the person it hit, when you were
   * the one driving. Small, and per hit: one is an accident, a drive through
   * the palo verdes is a choice. The same for both, by D's call. Read off personal standing, so it
   * lands only on people who have an opinion of you (the crew).
   */
  const REGARD_STRUCK = -2;

  /**
   * In the driver's seat: getting out, and who else is along for it.
   *
   * Anybody on the floor near enough to the empty seat, with nothing between
   * them and it, can be offered a lift — a colleague, a gate, a guest on the way
   * to the courts. Whether they would say yes is not modelled; they get in.
   * What is modelled is that they are a person the whole way: in the seat they
   * see what they are facing, the branches reach them, and when they get out
   * they walk back to where they were.
   */
  /** A short walk to the cart, with nothing in the way of it. */
  const LIFT_REACH = ft(15);

  function offerSeats(cart) {
    const out = [
      {
        id: 'cart-dismount',
        tier: NEAR,
        kind: CART,
        distance: 0,
        verb: 'get out of',
        what: 'the cart',
        run() {
          getOut(cart);
        },
      },
    ];

    for (const { seat, who } of wheeled.occupants(cart)) {
      if (seat === 'driver') continue;
      const npc = npcs.find((candidate) => candidate.data.id === who);
      if (!npc) continue;
      out.push({
        id: `cart-drop:${who}`,
        tier: POSSIBLE,
        kind: CART,
        distance: 0.02,
        verb: 'let',
        what: `${npc.data.name} off`,
        run() {
          letOff(who);
        },
      });
    }

    const free = wheeled.freeSeat(cart);
    if (free) {
      const at = wheeled.seatAt(cart, free.id);
      let best = null;
      for (const npc of npcs) {
        if (!npc.present || npc.aboard || npc.heldBack) continue;
        const gap = Math.hypot(npc.position.x - at.x, npc.position.z - at.z);
        if (gap > LIFT_REACH || (best && gap >= best.gap)) continue;
        // They have to be able to get to it, and walk back from it.
        if (!segmentClear({ x: npc.position.x, y: 0.5, z: npc.position.z }, { x: at.x, y: 0.5, z: at.z }, level.world)) continue;
        best = { npc, gap };
      }
      if (best) {
        out.push({
          id: `cart-lift:${best.npc.data.id}`,
          tier: POSSIBLE,
          kind: CART,
          distance: 0.01,
          verb: 'give',
          what: `${best.npc.data.name} a lift`,
          run() {
            giveLift(cart, free.id, best.npc);
          },
        });
      }
    }
    return out;
  }

  /** Who is riding with you, where they got on, and the way the ride went. */
  const riders = new Map();

  function giveLift(cart, seatId, npc) {
    if (!wheeled.seat(cart, seatId, npc.data.id)) return false;
    const from = { x: npc.position.x, z: npc.position.z };
    npc.board(() => wheeled.seatAt(cart, seatId));
    riders.set(npc.data.id, { cart, from, trail: [{ x: cart.position.x, z: cart.position.z }] });
    hud.say(`${sentence(whom(npc.data.name))} gets in the back.`, { tone: 'info', seconds: 2 });
    return true;
  }

  /**
   * Out, and on their way back. The trail is the ride backwards and then the
   * step from the cart to where they got in, which was clear or they could not
   * have got in.
   */
  function letOff(who) {
    wheeled.unseat(who);
    const ride = riders.get(who);
    riders.delete(who);
    const npc = npcs.find((candidate) => candidate.data.id === who);
    if (!npc) return;
    const trail = ride ? [...ride.trail].reverse().concat([ride.from]) : [];
    npc.alight(trail);
  }

  /** Out of the driver's seat, on the driver's side, and everybody else out too. */
  function getOut(cart) {
    const side = wheeled.seatAt(cart, 'driver');
    wheeled.release();
    for (const { who } of wheeled.occupants(cart)) letOff(who);
    if (side) {
      const r = wheeled.radius(cart) + player.tuning.radius + 0.2;
      // Across is (cos, -sin); the driver sits on the left of it.
      player.teleport({
        x: cart.position.x - Math.cos(cart.yaw) * r,
        y: cart.position.y,
        z: cart.position.z + Math.sin(cart.yaw) * r,
      });
    }
    hud.say('Out.', { tone: 'info', seconds: 1.5 });
    autosave.touch();
  }

  /** Every frame: the trail, and anybody who is no longer there to sit. */
  function runRiders() {
    for (const [who, ride] of [...riders]) {
      const npc = npcs.find((candidate) => candidate.data.id === who);
      const still = ride.cart.seats.some((seat) => seat.occupant === who);
      if (!npc || !npc.present || !still) {
        letOff(who);
        continue;
      }
      const last = ride.trail[ride.trail.length - 1];
      if (Math.hypot(ride.cart.position.x - last.x, ride.cart.position.z - last.z) > ft(3)) {
        ride.trail.push({ x: ride.cart.position.x, z: ride.cart.position.z });
      }
    }
  }

  /**
   * Struck by something growing over the path. The impact is on the bus, where
   * it is anybody's to act on; what is done with it here is that the person it
   * happened to says so, because a passenger is a person, and they hold it
   * against you if you were driving.
   */
  bus.on(EVENT.IMPACT, (event) => {
    if (event.target?.type !== 'body' || (event.kind !== 'branch' && event.kind !== 'bush')) return;
    const npc = npcs.find((candidate) => candidate.data.id === event.target.id);
    if (!npc) return;
    npc.say(event.kind === 'branch' ? 'Branch! Branch!' : 'Ow. Watch the bushes.', 3);
    if (event.actor === 'player') think(npc.data.id, REGARD_STRUCK, `You drove them through a ${event.kind}`);
  });

  return { offerSeats, run: runRiders, getOut, letOff };
}
